import type { PortalTranslations } from '../i18n'

interface LangToggleProps {
  lang: string
  allTranslations: Record<string, PortalTranslations>
  languages: string[]
}

/**
 * Inline language switcher for SSR portal pages.
 *
 * Renders a `<select>` listing every configured language with the current
 * one preselected, embeds all translation sets, and on `change` swaps the
 * page text in place plus updates hidden `lang` fields in all forms so
 * subsequent POSTs carry the new language.
 *
 * Replaces the previous "cycle" button — with 3+ languages the cycle was
 * painful (you couldn't see what you'd land on) and the button label only
 * showed the *next* code, never the current one.
 */
export function LangToggle({
  lang,
  allTranslations,
  languages,
}: LangToggleProps) {
  if (languages.length <= 1) return null

  const inlineScript = `
(function() {
  var tr = ${JSON.stringify(allTranslations)};
  var sel = document.getElementById('portal-lang-select');
  if (!sel) return;
  sel.addEventListener('change', function() {
    var current = sel.value;
    var t = tr[current];
    if (!t) return;
    document.querySelectorAll('[data-i18n]').forEach(function(el) {
      var key = el.getAttribute('data-i18n');
      var parts = key.split('.');
      var val = t;
      for (var i = 0; i < parts.length; i++) { val = val && val[parts[i]]; }
      if (val != null) {
        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
          el.setAttribute('placeholder', val);
        } else {
          el.textContent = val;
        }
      }
    });
    document.querySelectorAll('input[name="lang"]').forEach(function(el) {
      el.value = current;
    });
  });
})();
`

  // The customer-overridable `langSwitch` label (theme editor → Content tab,
  // global field) is used as both the visible label beside the dropdown and
  // its aria-label. Derived from `allTranslations[lang]` so we don't need a
  // redundant `translations` prop. Falls back to "Language" if no translation
  // is provided.
  const switchLabel = (
    allTranslations[lang] as { langSwitch?: string } | undefined
  )?.langSwitch
  const labelText =
    switchLabel && switchLabel.trim().length > 0 ? switchLabel : 'Language'

  return (
    <>
      <div className="portal-lang">
        <select
          id="portal-lang-select"
          className="portal-lang__select"
          defaultValue={lang}
          aria-label={labelText}
        >
          {languages.map((code) => (
            <option key={code} value={code}>
              {code.toUpperCase()}
            </option>
          ))}
        </select>
      </div>
      <script dangerouslySetInnerHTML={{ __html: inlineScript }} />
    </>
  )
}
